import { useEffect, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { Send, Loader2, Search, Users, Crown, MousePointerClick, Smartphone, MessageCircle, Mail, Paperclip } from "lucide-react";
import { toast } from "sonner";

const LINK_VARS = ["payment_link", "subscription_link", "ticket_link", "ticket_retrieval_link", "vod_link", "login_link", "receipt_link", "support_link", "promo_link"];

export function AdminBroadcast() {
  const [channel, setChannel] = useState("sms");
  const [audience, setAudience] = useState("all");
  const [counts, setCounts] = useState({});
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("document");
  const [sending, setSending] = useState(false);
  const [jobs, setJobs] = useState([]);

  // selected customers
  const [customers, setCustomers] = useState([]);
  const [picked, setPicked] = useState({});
  const [q, setQ] = useState("");
  const poll = useRef(null);

  const loadCounts = async () => { try { const { data } = await endpoints.broadcastCounts(); setCounts(data); } catch {} };
  const loadJobs = async () => { try { const { data } = await endpoints.broadcastList(); setJobs(data.broadcasts || []); } catch {} };
  const loadCustomers = async () => {
    try {
      const { data } = await endpoints.adminUsers?.() ?? {};
      const list = Array.isArray(data) ? data : (data?.users || []);
      setCustomers(list);
    } catch {}
  };
  useEffect(() => { loadCounts(); loadJobs(); }, []);
  useEffect(() => { if (audience === "selected" && customers.length === 0) loadCustomers(); }, [audience]);
  useEffect(() => () => poll.current && clearInterval(poll.current), []);

  const pickedIds = Object.keys(picked).filter((k) => picked[k]);
  const recipientEstimate = audience === "all" ? (channel === "email" ? counts.withEmail : counts.withPhone)
    : audience === "subscribers" ? counts.subscribers : pickedIds.length;

  const send = async () => {
    if (channel !== "email" && !message.trim()) { toast.error("Message is required"); return; }
    if (audience === "selected" && pickedIds.length === 0) { toast.error("Select at least one customer"); return; }
    setSending(true);
    try {
      const { data } = await endpoints.broadcastSend({
        channel, audience, userIds: pickedIds, subject, message,
        mediaUrl: mediaUrl.trim() || undefined, mediaType,
      });
      toast.success(`Sending to ${data.total} recipient(s)…`);
      setMessage(""); setMediaUrl("");
      loadJobs();
      poll.current = setInterval(async () => {
        const { data: st } = await endpoints.broadcastStatus(data.jobId);
        setJobs((js) => js.map((j) => (j.id === data.jobId ? st : j)));
        if (st.status === "done") { clearInterval(poll.current); loadJobs(); }
      }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const filtered = customers.filter((c) => {
    if (!q) return true;
    const s = `${c.displayName || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  }).slice(0, 200);

  const chBtn = (val, Icon, label) => (
    <button onClick={() => setChannel(val)} data-testid={`broadcast-channel-${val}`}
      className={`flex-1 h-11 rounded-xl border flex items-center justify-center gap-2 text-sm font-semibold transition ${channel === val ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#0F131A] border-[#273042] text-slate-300"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  const audBtn = (val, Icon, label, n) => (
    <button onClick={() => setAudience(val)} data-testid={`broadcast-audience-${val}`}
      className={`flex-1 h-16 rounded-xl border flex flex-col items-center justify-center gap-0.5 text-sm transition ${audience === val ? "bg-[#E11D2A]/15 border-[#E11D2A] text-white" : "bg-[#0F131A] border-[#273042] text-slate-300"}`}>
      <Icon className="w-4 h-4" /> <span className="font-semibold">{label}</span>
      {n != null && <span className="text-[11px] text-slate-400">{n} people</span>}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="admin-broadcast">
      <div>
        <label className="text-sm font-semibold text-white mb-2 block">Channel</label>
        <div className="flex gap-2">{chBtn("sms", Smartphone, "SMS")}{chBtn("whatsapp", MessageCircle, "WhatsApp")}{chBtn("email", Mail, "Email")}</div>
      </div>

      <div>
        <label className="text-sm font-semibold text-white mb-2 block">Audience</label>
        <div className="flex gap-2">
          {audBtn("all", Users, "All", channel === "email" ? counts.withEmail : counts.withPhone)}
          {audBtn("subscribers", Crown, "Subscribers", counts.subscribers)}
          {audBtn("selected", MousePointerClick, "Selected", pickedIds.length)}
        </div>
      </div>

      {audience === "selected" && (
        <div className="rounded-xl bg-[#0F131A] border border-[#273042] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / email" data-testid="broadcast-customer-search"
              className="flex-1 h-9 bg-transparent text-white text-sm outline-none" />
            <span className="text-xs text-slate-400">{pickedIds.length} selected</span>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-[#1b2330]">
            {filtered.map((c) => {
              const id = c.id || c._id;
              return (
                <label key={id} className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`broadcast-customer-${id}`}>
                  <input type="checkbox" checked={!!picked[id]} onChange={(e) => setPicked((p) => ({ ...p, [id]: e.target.checked }))}
                    className="w-4 h-4 accent-[#E11D2A]" />
                  <span className="text-sm text-white">{c.displayName || "—"}</span>
                  <span className="text-xs text-slate-500 ml-auto">{channel === "email" ? c.email : c.phone}</span>
                </label>
              );
            })}
            {filtered.length === 0 && <p className="text-xs text-slate-500 py-3 text-center">No customers found.</p>}
          </div>
        </div>
      )}

      {channel === "email" && (
        <div>
          <label className="text-sm font-semibold text-white mb-1 block">Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject"
            className="w-full h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
        </div>
      )}

      <div>
        <label className="text-sm font-semibold text-white mb-1 block">Message</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="broadcast-message"
          placeholder="Type your message. You can use links like {payment_link}."
          className="w-full rounded-xl bg-[#0A0C10] border border-[#273042] px-3 py-2 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
        <div className="flex flex-wrap gap-1 mt-1">
          {LINK_VARS.map((v) => (
            <button key={v} onClick={() => setMessage((m) => `${m}${m && !m.endsWith(" ") ? " " : ""}{${v}}`)}
              className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 font-mono hover:bg-white/10">+ {`{${v}}`}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2">
          <label className="text-sm font-semibold text-white mb-1 block flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Attachment URL (optional)</label>
          <input value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} data-testid="broadcast-media-url"
            placeholder="https://… (document / video / image)"
            className="w-full h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
        </div>
        <div>
          <label className="text-sm font-semibold text-white mb-1 block">Type</label>
          <select value={mediaType} onChange={(e) => setMediaType(e.target.value)} data-testid="broadcast-media-type"
            className="w-full h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none">
            <option value="document">Document</option>
            <option value="video">Video</option>
            <option value="image">Image</option>
          </select>
        </div>
      </div>

      <button onClick={send} disabled={sending} data-testid="broadcast-send-btn"
        className="h-12 px-6 rounded-full bg-[#E11D2A] text-white font-semibold flex items-center gap-2 disabled:opacity-60">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send to {recipientEstimate ?? "…"} {channel === "email" ? "email(s)" : "phone(s)"}
      </button>

      {jobs.length > 0 && (
        <div className="border-t border-[#273042] pt-4" data-testid="broadcast-jobs">
          <h3 className="text-sm font-semibold text-white mb-2">Recent broadcasts</h3>
          <div className="space-y-1.5">
            {jobs.map((j) => (
              <div key={j.id} className="flex items-center gap-3 text-xs text-slate-400 bg-[#0F131A] border border-[#273042] rounded-lg px-3 py-2">
                <span className="uppercase font-semibold text-slate-300">{j.channel}</span>
                <span>· {j.audience}</span>
                <span className="ml-auto">{j.sent}/{j.total} sent{j.failed ? `, ${j.failed} failed` : ""}</span>
                <span className={`px-2 py-0.5 rounded-full ${j.status === "done" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>{j.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
